import React from 'react';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import { makeStyles } from '@material-ui/core/styles';

import { CustomTable } from 'components/elements';
import { ActionButton, EventType } from 'components/common';
import { getState, checkAssigned } from 'utils';
import TasksToolbar from './ToolBar';
import {isComponentVisibleForUser, isComponentEnabledForUser} from 'utils';


const useStyles = makeStyles((theme) => ({
  container: {
    maxWidth: '100%',

    '& button': {
      border: `1px solid ${theme.palette.base.white}`,
    },
  },
  attachment: {
    width: 30,
    height: 30,
    cursor: 'pointer',
  },
  taskId: {
    cursor: 'pointer',
  },
  rounded: {
    borderRadius: '10px'
  }
}));

const cellStyle = (rowData) => ({
  paddingLeft: 15,
  paddingRight: 4,
  borderRight: '1px solid white',
  lineHeight: '19px',
  // color: checkAssigned(rowData) ? '#1B1B28' : '#FFFFFF',
  color: '#1B1B28',
  textAlign: 'left',
});

function TasksTable(props) {
  const classes = useStyles();
  const { t } = useTranslation();

  const {
    parkingTasks,
    tasksPaging,
    state,
    taskHosts,
    createdByName,
    users,
    hosts,
    isLoading,
    handleState,
    handleSelectHosts,
    handleUser,
    handleSearch,
    handleAddTask,
    handleTaskDetailOpen,
    handleCancel,
    handleRenew,
    handleChangeRowsPerPage,
    handleChangePage,
    userUseCases,
  } = props;

  const columns = [
    {
      title: t('TASK_ID'),
      field: 'taskId',
      cellStyle: (data, rowData) => ({
        ...cellStyle(rowData),
        borderLeft: '1px solid white',
      }),
      headerStyle: { borderTopLeftRadius: '10px', borderBottomLeftRadius: '10px'},
      render: (rowData) => (
        isComponentVisibleForUser(UC_TASK_DETAIL, userUseCases) ? (
        <div
          className={classes.taskId}
          onClick={isComponentEnabledForUser(UC_TASK_DETAIL, userUseCases) ? handleTaskDetailOpen({
            task: rowData,
            image: rowData.image ? rowData.image.file : null,
            imageModal: false,
          }) : ()=>{}}
        >
          {rowData.taskId}
        </div>
          ) : (rowData.taskId)
      ),
    },
    {
      title: t('STATE'),
      field: 'status',
      cellStyle: (data, rowData) => cellStyle(rowData),
      render: (rowData) => (
        <EventType type={getState(rowData.status, t)} />
      ),
    },
    { title: t('CREATION_DATE'), field: 'creationDate', cellStyle: (data, rowData) => cellStyle(rowData) },
    { title: t('CREATED_BY'), field: 'creationBy', cellStyle: (data, rowData) => cellStyle(rowData) },
    { title: t('ASSIGNED_TO'), field: 'solver', cellStyle: (data, rowData) => cellStyle(rowData) },
    { title: t('HOST'), field: 'parkingHostName', cellStyle: (data, rowData) => cellStyle(rowData) },
    { title: t('DESCRIPTION'), field: 'description', cellStyle: (data, rowData) => cellStyle(rowData) },
    {
      title: t('ACTION'),
      field: 'action',
      cellStyle: (data, rowData) => cellStyle(rowData),
      render: (rowData) => (
        rowData.status === 'CA' && isComponentVisibleForUser(UC_TASK_CANCEL, userUseCases) ? 
        <ActionButton
          disabled={!isComponentEnabledForUser(UC_TASK_CREATE, userUseCases)}
          action={rowData.status === 'CA' ? 'renew' : ''}
          handleClick={rowData.status === 'CA' ? handleRenew(rowData) : null }
          className={classes.rounded}
        /> 
        : 
        isComponentVisibleForUser(UC_TASK_CREATE, userUseCases) ? 
        <ActionButton
          disabled={!isComponentEnabledForUser(UC_TASK_CANCEL, userUseCases)}
          action={rowData.status === 'CO' || rowData.status === 'CA' ? '' : 'cancel'}
          handleClick={rowData.status === 'CO' || rowData.status === 'CA' ? null : handleCancel(rowData)}
          className={classes.rounded}
        /> : null          
      ),
    },
    {
      title: t('ATTACHMENT'),
      field: 'image',
      cellStyle: (data, rowData) => cellStyle(rowData),
      headerStyle: { borderTopRightRadius: '10px', borderBottomRightRadius: '10px'},
      render: (rowData) => (rowData.image ? (
        <img
          src={`data:image/png;base64,${rowData.image.file}`}
          className={classes.attachment}
          onClick={handleTaskDetailOpen({
            task: rowData,
            image: rowData.image.file,
            imageModal: true,
          })}
          alt=""
        />
      ) : null),
    },
  ];

  return (
    <div className={classes.container}>
      <TasksToolbar
        taskHosts={taskHosts}
        tasks={parkingTasks}
        state={state}
        selectedHosts={hosts}
        createdByName={createdByName}
        users={users}
        handleState={handleState}
        handleSelectHosts={handleSelectHosts}
        handleUser={handleUser}
        handleSearch={handleSearch}
        handleAddTask={handleAddTask}
        userUseCases={userUseCases}
      />

      <CustomTable
        title=""
        columns={columns}
        data={parkingTasks}
        options={{
          headerStyle: {
            padding: '15px 8px 15px 15px',
            // borderTop: '1px solid #DEE2E5',
            lineHeight: '12px',
            color: '#1B1B28',
            textAlign: 'left',
            borderBottom: 'none',
            backgroundColor: '#F5F5F5',
          },
          // rowStyle: (data) => ({
          //   background: checkAssigned(data) ? 'white' : '#f5d2ceff',
          // }),
        }}
        isLoading={isLoading}
        rowsPerPage={tasksPaging.pageSize}
        pageNumber={tasksPaging.currentPage}
        handleChangeRowsPerPage={handleChangeRowsPerPage}
        handleChangePage={handleChangePage}
        count={tasksPaging.totalSize}
        loadAllData
      />
    </div>
  );
}

TasksTable.propTypes = {
  parkingTasks: PropTypes.array.isRequired,
  tasksPaging: PropTypes.object.isRequired,
  taskHosts: PropTypes.array.isRequired,
  isLoading: PropTypes.bool.isRequired,
  createdByName: PropTypes.array.isRequired,
  users: PropTypes.array.isRequired,
  state: PropTypes.array.isRequired,
  hosts: PropTypes.array.isRequired,
  handleState: PropTypes.func.isRequired,
  handleSelectHosts: PropTypes.func.isRequired,
  handleUser: PropTypes.func.isRequired,
  handleSearch: PropTypes.func.isRequired,
  handleAddTask: PropTypes.func.isRequired,
  handleTaskDetailOpen: PropTypes.func.isRequired,
  handleCancel: PropTypes.func.isRequired,
  handleRenew: PropTypes.func.isRequired,
  handleChangeRowsPerPage: PropTypes.func.isRequired,
  handleChangePage: PropTypes.func.isRequired,
};

const UC_TASK_DETAIL = 'UC0036';
const UC_TASK_CANCEL = 'UC0037';
const UC_TASK_CREATE = 'UC0038';

export default TasksTable;
